import {useAuth} from '@common/auth/use-auth';
import {UnstyledCustomMenuItem} from '@common/menus/custom-menu';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {NotificationsDialog} from '@common/notifications/notifications-dialog';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {NavbarAuthButtons} from '@common/ui/navigation/navbar/navbar-auth-buttons';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import {CommandPaletteTrigger} from '@common/ui/command-palette/command-palette';
import {SitePreferences} from '@common/regions/site-preferences';
import {Button, LinkButton} from '@shadcn/button/button';
import {Drawer} from '@shadcn/drawer/drawer';
import {Dropdown} from '@shadcn/dropdown/dropdown';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {useSettings} from '@ui/settings/use-settings';
import {cn} from '@ui/utils/cn';
import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';
import {MenuIcon} from 'lucide-react';
import {ComponentProps} from 'react';
import {Link, useLocation} from 'react-router';

export const siteNavItems = [
  {to: '/pages/about-us', label: 'About'},
  {to: '/pricing', label: 'Pricing'},
  {to: '/developers', label: 'API'},
  {to: '/contact', label: 'Contact'},
] as const;

function Root({
  children,
  className,
  variant = 'app',
  ...props
}: ComponentProps<'div'> & {variant?: 'app' | 'site'}) {
  return (
    <div
      className={cn(
        'flex h-16 min-w-0 shrink-0 items-center gap-2 bg-background px-3 py-2 md:gap-5 md:px-5',
        className,
      )}
      {...props}
    >
      {variant === 'site' ? <SiteNavbarContents /> : children}
    </div>
  );
}

function NavbarLogo({className, ...props}: ComponentProps<typeof Logo>) {
  return (
    <Logo
      className={cn('h-9 max-h-9 max-w-full min-w-0 overflow-hidden md:h-11 md:max-h-11 md:max-w-60', className)}
      {...props}
    />
  );
}

function NavbarContent({children, className, ...props}: ComponentProps<'div'>) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)} {...props}>
      {children}
    </div>
  );
}

function NavbarAuthContent({
  menuItems,
  className,
  hideNotifsOnMobile = false,
  hideAppearance = false,
}: {
  menuItems?: NavbarAuthUserProps['items'];
  className?: string;
  hideNotifsOnMobile?: boolean;
  hideAppearance?: boolean;
}) {
  const isMobile = useIsMobileMediaQuery();
  const {notifications} = useSettings();
  const {isLoggedIn} = useAuth();
  const showNotifButton =
    isLoggedIn && notifications?.integrated && !hideNotifsOnMobile;
  return (
    <NavbarContent className={cn(className, 'gap-1 md:gap-3')}>
      {hideAppearance ? null : <SitePreferences compact />}
      {showNotifButton && <NotificationsDialog />}
      {isLoggedIn ? (
        <NavbarAuthUser
          variant={isMobile ? 'compact' : 'wide'}
          items={menuItems}
        />
      ) : (
        <NavbarAuthButtons />
      )}
    </NavbarContent>
  );
}

function NavbarMenu({
  position,
  className,
}: {
  position: string;
  className?: string;
}) {
  const menuConfig = useCustomMenu(position);

  if (!menuConfig) return null;

  return (
    <div className={className}>
      <div className="mx-4 hidden items-center gap-4 text-sm text-muted-foreground md:flex">
        {menuConfig?.items.map(item => (
          <UnstyledCustomMenuItem
            key={item.id}
            item={item}
            className={({isActive}) =>
              cn(
                'hover:text-foreground hover:underline',
                isActive && 'text-foreground',
              )
            }
          />
        ))}
      </div>
      <Dropdown.Root>
        <Dropdown.Trigger
          render={<Button variant="ghost" size="icon" />}
          className="md:hidden"
        >
          <MenuIcon />
        </Dropdown.Trigger>
        <Dropdown.Content>
          {menuConfig.items.map(item => (
            <Dropdown.LinkItem key={item.id} render={<Link to={item.action} />}>
              <Trans message={item.label} />
            </Dropdown.LinkItem>
          ))}
        </Dropdown.Content>
      </Dropdown.Root>
    </div>
  );
}

function usePublicNavItems() {
  const menu = useCustomMenu('landing-page-navbar');
  if (menu?.items?.length) {
    return menu.items.map(item => ({
      to: item.action,
      label: item.label,
      id: item.id,
    }));
  }
  return siteNavItems.map(item => ({
    to: item.to,
    label: item.label,
    id: item.to,
  }));
}

function SiteNavbarContents() {
  const {isLoggedIn} = useAuth();
  const {pathname} = useLocation();
  const navItems = usePublicNavItems();

  return (
    <>
      <NavbarLogo url="/" />
      <nav className="mx-2 hidden min-w-0 items-center gap-5 text-sm text-muted-foreground md:flex">
        {navItems.map(item => (
          <Link
            key={item.id}
            to={item.to}
            className={cn(
              'hover:text-foreground',
              pathname === item.to && 'font-medium text-foreground',
            )}
          >
            <Trans message={item.label} />
          </Link>
        ))}
      </nav>
      <NavbarContent className="ms-auto">
        <CommandPaletteTrigger className="hidden md:inline-flex" />
        <SitePreferences />
        <div className="hidden md:block">
          {isLoggedIn ? (
            <NavbarAuthUser />
          ) : (
            <NavbarAuthButtons />
          )}
        </div>
        {isLoggedIn ? (
          <div className="md:hidden">
            <NavbarAuthUser variant="compact" />
          </div>
        ) : null}
        <div className="md:hidden">
          <MobileSiteMenu />
        </div>
      </NavbarContent>
    </>
  );
}

function MobileSiteMenu() {
  const {isLoggedIn} = useAuth();
  const {registration} = useSettings();
  const {pathname} = useLocation();
  const {trans} = useTrans();
  const registrationEnabled = !registration?.disable;
  const navItems = usePublicNavItems();
  const direction = useBootstrapDataStore(s => s.data.i18n?.direction);
  // Start edge: LTR = left, RTL = right (reading start).
  const drawerSide = direction === 'rtl' ? 'right' : 'left';

  return (
    <Drawer.Root position={drawerSide}>
      <Drawer.Trigger
        render={<Button variant="ghost" size="icon" />}
        aria-label={trans({message: 'Open menu'})}
      >
        <MenuIcon />
      </Drawer.Trigger>
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Content>
          <Drawer.Header>
            <Drawer.Title>
              <Trans message="Cloud Drive" />
            </Drawer.Title>
            <Drawer.Description>
              <Trans message="Cloud Storage Online" />
            </Drawer.Description>
          </Drawer.Header>
          <Drawer.Body>
            <nav className="flex flex-col gap-0.5">
              {navItems.map(item => (
                <Drawer.Close
                  key={item.id}
                  render={
                    <Link
                      to={item.to}
                      className={cn(
                        'rounded-lg px-3 py-2 text-sm hover:bg-muted',
                        pathname === item.to && 'bg-muted font-medium',
                      )}
                    />
                  }
                >
                  <Trans message={item.label} />
                </Drawer.Close>
              ))}
            </nav>
          </Drawer.Body>
          {!isLoggedIn ? (
            <Drawer.Footer>
              <Drawer.Close
                render={<LinkButton to="/login" variant="outline" className="w-full" />}
              >
                <Trans message="Sign in" />
              </Drawer.Close>
              {registrationEnabled ? (
                <Drawer.Close
                  render={<LinkButton to="/register" className="w-full" />}
                >
                  <Trans message="Sign up" />
                </Drawer.Close>
              ) : null}
            </Drawer.Footer>
          ) : null}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export const Navbar = Object.assign(Root, {
  Root,
  Logo: NavbarLogo,
  Menu: NavbarMenu,
  Content: NavbarContent,
  AuthContent: NavbarAuthContent,
});
